import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation} from "react-router-dom";
import { FaCreditCard, FaUniversity } from "react-icons/fa";
import { CreditCard, FileText, Sun } from "lucide-react";

// Si tenés un hook global:
import { useCarrito } from "../context/CarritoContext";


export default function Pago() {
  const location = useLocation();

  const {
    carrito: carritoStore,
    provincia: provinciaStore,
    cp: cpStore,
    metodoEntrega: metodoEntregaStore,
    servicio: servicioStore,
    precioEnvio: precioEnvioStore,
  } = useCarrito();

const COUNTDOWN_MS = 10 * 60 * 1000;
const deadlineRef = useRef(Date.now() + COUNTDOWN_MS);
const [remainingMs, setRemainingMs] = useState(deadlineRef.current - Date.now());

useEffect(() => {
  const id = setInterval(() => {
    const ms = deadlineRef.current - Date.now();
    setRemainingMs(ms > 0 ? ms : 0);
  }, 1000);
  return () => clearInterval(id);
}, []);

const mm = Math.floor(remainingMs / 60000);
const ss = Math.floor((remainingMs % 60000) / 1000);
const mmss = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;


  // Datos desde Checkout con fallback al store
  const {
    carrito = carritoStore || [],
    provincia = provinciaStore || "",
    localidad = location.state?.localidad || "",
    codigoPostal = location.state?.codigoPostal ?? cpStore ?? "",
    metodoEntrega = metodoEntregaStore || "domicilio",
    servicio = location.state?.servicio ?? servicioStore ?? null,
    precioEnvio = typeof location.state?.precioEnvio === "number"
      ? location.state.precioEnvio
      : (precioEnvioStore ?? null),

    // Cliente
    nombre = location.state?.nombre || "",
    email = location.state?.email || "",
    telefono = location.state?.telefono || "",
    dni = location.state?.dni || "",

    // Domicilio
    calle = location.state?.calle || "",
    numero = location.state?.numero || "",
    pisoDepto = location.state?.pisoDepto || "",
    referencia = location.state?.referencia || "",

    // Sucursal
    sucursalSeleccionada = location.state?.sucursalSeleccionada || null,
  } = location.state || {};

  const [paymentMethod, setPaymentMethod] = useState("mp"); // "mp" | "transfer"
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferInstr, setTransferInstr] = useState(null); // instrucciones del server

  // 🔹 estado de carga para el botón
  const [loading, setLoading] = useState(false);

  // Totales (SOLO informativos para UI)
  const parsePrecio = (v) => {
  if (typeof v === "number") return v;
  const s = String(v ?? "").replace(/[^\d.,-]/g, "");
  const n = parseFloat(s.replace(/\./g, "").replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
};

const getQty = (it) => Math.max(1, Number(it?.cantidad ?? it?.qty ?? it?.quantity ?? 1));

const precio30Off = (n) => Math.floor((n * 0.70) / 100) * 100;

const totalProductos = useMemo(() => {
  return (carrito || []).reduce((acc, item) => {
    const precioOriginal = parsePrecio(item.precio);
    const precioConOff = precio30Off(precioOriginal);
    const qty = getQty(item);
    return acc + precioConOff * qty;
  }, 0);
}, [carrito]);

  const FREE_SHIPPING_THRESHOLD = 175000;
  const envioGratisElegible = totalProductos >= FREE_SHIPPING_THRESHOLD;
  const precioEnvioFinal =
    metodoEntrega === "domicilio" && envioGratisElegible
      ? 0
      : (precioEnvio ?? 0);

  const total =
    precioEnvio == null && !(metodoEntrega === "domicilio" && envioGratisElegible)
      ? totalProductos
      : totalProductos + precioEnvioFinal;

  const totalTransfer =
    (totalProductos * 0.85) +
    ((precioEnvio == null && !(metodoEntrega === "domicilio" && envioGratisElegible))
      ? 0
      : precioEnvioFinal);

  // Entrega
  const direccionCompleta = useMemo(() => {
    const base = `${(calle || "").trim()} ${(numero || "").trim()}`.trim();
    const extra = pisoDepto ? `, ${pisoDepto.trim()}` : "";
    return (base + extra).trim();
  }, [calle, numero, pisoDepto]);

  const entregaResumen = useMemo(() => {
    if (metodoEntrega === "sucursal" && sucursalSeleccionada) {
      return {
        titulo: "Retiro en sucursal",
        detalle: `${sucursalSeleccionada?.nombre} — ${sucursalSeleccionada?.address || ""}`,
      };
    }
    if (metodoEntrega === "domicilio" && calle && numero && provincia && localidad) {
      return {
        titulo: "Entrega a domicilio",
        detalle: `${calle} ${numero}${pisoDepto ? ", " + pisoDepto : ""}, ${localidad}, ${provincia}`,
      };
    }
    return { titulo: "Método de entrega", detalle: "Faltan datos. Volvé al checkout." };
  }, [metodoEntrega, sucursalSeleccionada, calle, numero, pisoDepto, localidad, provincia]);

  // ✅ Validación MP: SIN exigir precioEnvio (el back calcula el envío)
  const datosCompletosParaMP =
    carrito?.length > 0 &&
    nombre && email && dni &&
    provincia && (localidad || metodoEntrega === "sucursal") &&
    codigoPostal &&
    (metodoEntrega === "domicilio" ? (calle && numero) : sucursalSeleccionada);

  // =========================
  // [LS] Persistencia local del orderId de TRANSFERENCIA
  // =========================
  const LS_KEY = "transferOrder";
  const HOLD_MS = 30 * 60 * 1000; // 30 minutos

  const cartHash = useMemo(() => {
    try {
      const lite = (carrito || []).map(({ nombre, talle, precio, cantidad }) => ({
        n: nombre, t: talle, p: precio, q: cantidad ?? 1,
      }));
      return btoa(unescape(encodeURIComponent(JSON.stringify(lite))));
    } catch {
      return "";
    }
  }, [carrito]);

  const loadStored = () => {
    try { const raw = localStorage.getItem(LS_KEY); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  };
  const saveStored = (obj) => { try { localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch {} };
  const clearStored = () => { try { localStorage.removeItem(LS_KEY); } catch {} };

  useEffect(() => {
    const s = loadStored();
    if (!s) return;
    if (s.cartHash && s.cartHash !== cartHash) { clearStored(); }
  }, [cartHash]);

  useEffect(() => {
    if (paymentMethod !== "transfer") { clearStored(); }
  }, [paymentMethod]);

  const onConfirmar = async () => {
    const isMP = paymentMethod === "mp";

    if (isMP && !datosCompletosParaMP) {
      alert("Faltan datos. Volvé al checkout y completá todo.");
      return;
    }

    window.fbq?.("track", "AddPaymentInfo", {
      value: paymentMethod === "transfer" ? totalTransfer : total,
      currency: "ARS",
    });

    setLoading(true);

    // [LS] Decidir orderId a usar (reusar si vigente y carrito igual)
    let stored = !isMP ? loadStored() : null;
    const now = Date.now();
    const validStored =
      stored &&
      stored.orderId &&
      stored.expiresAt &&
      now < stored.expiresAt &&
      stored.cartHash === cartHash;

    const orderId = !isMP && validStored
      ? stored.orderId
      : `WET-${Date.now().toString(36).toUpperCase()}`;

    if (!isMP && !validStored) {
      saveStored({ orderId, createdAt: now, expiresAt: now + HOLD_MS, cartHash });
    }

    const envioPayload = {
      metodoEntrega,
      servicio: servicio?.code || servicio?.nombre || servicio || "",
      telefono,
      direccion: metodoEntrega === "domicilio" ? direccionCompleta : "",
      calle,
      numero,
      pisoDepto,
      referencia,
      localidad: (localidad || ""),
      provincia: (provincia || ""),
      cp: (codigoPostal || ""),
      sucursal: metodoEntrega === "sucursal"
        ? `${sucursalSeleccionada?.nombre || ""}${sucursalSeleccionada?.address ? " — " + sucursalSeleccionada.address : ""}`
        : ""
    };

    try {
      const response = await fetch(
        "https://script.google.com/macros/s/AKfycbzSlr8REtr3Z7NXK4oKkz9r8FQpubIqpQI64iyCcCSG7BV8_PTOvXumbbVhIpwhCZHV/exec",
        {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            action: "create",
            paymentMethod,
            orderId,                      // reuso si vigente
            carrito,                      // NO mandamos precios x item al back (él los lee de inventario)
            nombre, email, telefono, dni,
            ...envioPayload               // datos de envío normalizados
            // ❌ NO enviar totalFinal ni totalTransfer (los calcula el back)
          }),
        }
      );

      const data = await response.json();

      if (isMP) {
        if (data.init_point) {
          localStorage.setItem("orderId", orderId);
          window.location.href = data.init_point;
        } else {
          alert("❌ Error al generar el pago con Mercado Pago.");
        }
      } else {
        if (data.instrucciones) {
          setTransferInstr(data.instrucciones); // { alias, cbu, subtotal, descuento, envio, total, vence, orderId }
          setShowTransferModal(true);

          // Refrescar vencimiento local si backend devolvió otro
          try {
            const cur = loadStored();
            if (cur?.orderId === orderId) {
              const vence = new Date(data.instrucciones.vence).getTime();
              if (!isNaN(vence)) saveStored({ ...cur, expiresAt: vence });
            }
          } catch {}
        } else {
          alert("❌ Error al generar la orden de transferencia.");
        }
      }
    } catch (error) {
      alert("❌ Hubo un error al procesar tu pago.");
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp para transferencia (usa TOTAL DEL BACK si existe)
  const whatsNumber = "542215444828"; // cambiá por tu número
  const resumenItems = (carrito || []).map((it) => it.nombre).join(", ");
  const montoWA = transferInstr?.total ?? totalTransfer; // preferir total del back
  const waMsgFallback = `Hola! Te envío el comprobante de transferencia .
Monto: $${Number(montoWA).toLocaleString("es-AR",{minimumFractionDigits:2})}
Nombre: ${nombre || "-"}
DNI: ${dni || "-"}
Pedido: ${resumenItems || "-"}
Entrega: ${entregaResumen.detalle || "-"}`;
   const waHref = transferInstr?.waHref
   ? transferInstr.waHref
   : `https://wa.me/${whatsNumber}?text=${encodeURIComponent(waMsgFallback)}`;

  // Helpers UI
  const Price = ({ value, strong }) => (
    <span className={strong ? "font-bold text-gray-900" : "font-semibold"}>
      ${Number(value).toLocaleString("es-AR",{minimumFractionDigits:2, maximumFractionDigits:2})}
    </span>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 md:pt-36">
      <div className="mb-3 flex items-center justify-center gap-2">
        <h1 className="text-md nav-onepalm font-semibold text-yellow-900 text-center">
          Confirmá tu pedido
        </h1>
        <span
          className="inline-flex items-center gap-1 text-md font-bold  text-gray-900 px-2 py-1  "
          aria-live="polite"
        >
          🕒 {mmss}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 md:gap-8">
        {/* ==== MÉTODO DE PAGO ==== */}
        <section className="order-2 md:order-1 md:col-span-7 rounded-xl border bg-white p-4 mt-6 md:mt-0">
          <h2 className="text-xs nav-onepalm font-semibold mb-3">Método de pago</h2>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="payment"
                value="mp"
                checked={paymentMethod === "mp"}
                onChange={() => setPaymentMethod("mp")}
              />
              <FaCreditCard className="w-5 h-5" />
              <div>
                <div className="text-sm pd-onepalm">Mercado Pago</div>
                <div className="text-xs text-gray-700">3 cuotas sin interés.</div>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="payment"
                value="transfer"
                checked={paymentMethod === "transfer"}
                onChange={() => setPaymentMethod("transfer")}
              />
              <FaUniversity className="w-5 h-5" />
              <div>
                <div className="text-sm font-medium">Transferencia Bancaria</div>
                <div className="text-xs text-gray-700">15% de descuento.</div>
              </div>
            </label>
          </div>

          {/* Totales informativos (UI) */}
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 text-xs pd-onepalm">Subtotal</span>
                <span className="proba text-[10px] text-gray-700">
                  <Price value={totalProductos} />
                </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-700 text-xs">Envío</span>
              <span className="font-semibold proba text-[10px] text-green-700">
                {precioEnvio == null && !(metodoEntrega === "domicilio" && envioGratisElegible)
                  ? "—"
                  : (
                    <>${
                      Number(precioEnvioFinal).toLocaleString("es-AR",{minimumFractionDigits:2, maximumFractionDigits:2})
                    }</>
                  )}
              </span>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-gray-900 font-semibold">Total</span>
              <span className="text-xs proba text-gray-900">
                <Price value={total} />
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-800 pd-onepalm">
                En transferencia
              </span>
              <span className="text-xs proba font-semibold text-green-700">
                <Price value={totalTransfer} />
              </span>
            </div>
          </div>

          <div className="mt-5">
            <button
              onClick={onConfirmar}
              disabled={loading}
              aria-busy={loading}
              aria-live="polite"
              className={`w-full nav-onepalm text-sm font-semibold bg-[#ffae00] text-black px-6 py-3 shadow-md hover:shadow-lg hover:bg-[#e69c00] active:scale-95 transition
                ${loading ? "opacity-60 cursor-not-allowed pointer-events-none" : ""}`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {paymentMethod === "mp" ? "Redirigiendo a Mercado Pago…" : "Generando orden…"}
                </span>
              ) : (
                "Pagar"
              )}
            </button>
          </div>
        </section>

        {/* ==== TU PEDIDO ==== */}
        <section className="order-1 md:order-2 md:col-span-5 rounded-xl border bg-white p-4">
          <h2 className="text-sm font-semibold mb-0">Tu pedido</h2>

          <ul className="divide-y">
            {(carrito || []).map((item, i) => (
              <li key={i} className="py-3 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <img
                    src={item.img}
                    alt={item.nombre}
                    className="w-16 h-16 object-cover"
                  />
                  <div>
                    <div className="text-xs proba">{item.nombre}</div>
                    {item.talle && (
                      <div className="text-[9px] mt-1 proba text-gray-700">Talle: {item.talle}</div>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-700 proba">
                  ${Number(precio30Off(parsePrecio(item.precio))).toLocaleString("es-AR",{minimumFractionDigits:2, maximumFractionDigits:2})}
                  {getQty(item) > 1 ? ` x${getQty(item)}` : ""}
                </div>
              </li>
            ))}
          </ul>

          {/* Entrega */}
          <div className="mt-4">
            <div className="text-sm font-semibold mb-0">{entregaResumen.titulo}</div>
            <div className="text-sm text-gray-900">{entregaResumen.detalle}</div>
          </div>

          {/* Cliente */}
          <div className="mt-4">
            <div className="text-sm font-semibold mb-0">Datos del cliente</div>
            <div className="text-sm text-gray-900">
              <div><span className="font-medium">Nombre:</span> {nombre || "—"}</div>
              <div><span className="font-medium">Email:</span> {email || "—"}</div>
              <div><span className="font-medium">DNI:</span> {dni || "—"}</div>
            </div>
          </div>
        </section>
      </div>

      {/* Modal Transferencia (usa total del BACK) */}
      {showTransferModal && transferInstr && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5">
            <div className="flex items-start justify-between">
              <h4 className="text-lg font-semibold">Pago por transferencia</h4>
              <button
                onClick={() => setShowTransferModal(false)}
                className="text-gray-500 hover:text-black"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="mt-3 rounded-lg border border-yellow-400 bg-yellow-50 p-3">
              <div className="text-sm text-yellow-900 space-y-1">
                <p className="flex items-center gap-2">
                  1- Transferí el <span className="font-bold">monto exacto</span>
                  <CreditCard size={16} className="text-yellow-700" />
                </p>
                <p>
                  2- Enviá el <span className="font-bold">comprobante</span> por WhatsApp con tu{" "}
                  <span className="font-bold">número de orden</span>.
                  <FileText size={16} className="inline-block text-yellow-700 ml-1" />
                </p>
                <p className="flex items-center gap-2">
                  3- Disfrutá del verano con <span className="font-bold">WetSoul</span>
                  <Sun size={16} className="text-yellow-700" />
                </p>
              </div>
            </div>

            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-800 font-medium">Monto a transferir</span>
                <span className="font-semibold">
                  ${Number(transferInstr.total).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              <div className="mt-2 border rounded-lg p-3 bg-gray-50">
                <p className="text-sm text-gray-900 mb-1 font-semibold">Alias</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{transferInstr.alias}</span>
                  <button
                    type="button"
                    className="text-xs underline"
                    onClick={() => navigator.clipboard.writeText(transferInstr.alias)}
                  >
                    Copiar
                  </button>
                </div>

                <div className="mt-3">
                  <p className="text-sm text-gray-900 font-semibold mb-1">CBU / CVU</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm">{transferInstr.cbu}</span>
                    <button
                      type="button"
                      className="text-xs underline"
                      onClick={() => navigator.clipboard.writeText(transferInstr.cbu)}
                    >
                      Copiar
                    </button>
                  </div>
                </div>

                <div className="flex flex-col items-start mb-1 mt-1 gap-1">
                  <p className="text-sm text-gray-900 font-semibold">Numero de orden</p>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{transferInstr.orderId}</span>
                    <button
                      type="button"
                      className="text-xs underline"
                      onClick={() => navigator.clipboard.writeText(transferInstr.orderId)}
                    >
                      Copiar
                    </button>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-800">
                  <div><span className="font-medium">Banco:</span> Mercado Pago</div>
                  <div><span className="font-medium">Titular:</span> Mateo Gonzalo Lopez</div>
                  <div><span className="font-medium">Vence:</span> {new Date(transferInstr.vence).toLocaleString("es-AR")}</div>
                </div>
              </div>
            </div>

            {/* WhatsApp (usa total del BACK) */}
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 block w-full text-center rounded-full bg-green-600 text-white py-2 font-semibold hover:bg-green-700 active:scale-[0.99] transition"
            >
              Enviar comprobante por WhatsApp
            </a>

            <button
              className="mt-2 w-full rounded-full bg-black text-white py-2 font-semibold"
              onClick={() => setShowTransferModal(false)}
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}